import React, { useEffect, useRef, useState, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { FaPlay, FaPaperPlane, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { codeRun, codeSubmit, codeSaveDraft } from '../../api/publicAssessApi';
import ConfirmModal from '../common/ConfirmModal';

const LANGUAGE_META = {
  python: { label: 'Python 3', monaco: 'python' },
  java: { label: 'Java', monaco: 'java' },
  cpp: { label: 'C++', monaco: 'cpp' },
  c: { label: 'C', monaco: 'c' },
  javascript: { label: 'JavaScript (Node.js)', monaco: 'javascript' }
};
const ALL_LANGUAGES = Object.keys(LANGUAGE_META);

const STARTERS = {
  python: '# Read input with input(), print the answer with print()\n\n',
  java: 'import java.util.*;\n\npublic class Main {\n    public static void main(String[] args) {\n        Scanner sc = new Scanner(System.in);\n        // write your code here\n    }\n}\n',
  cpp: '#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n    // write your code here\n    return 0;\n}\n',
  c: '#include <stdio.h>\n\nint main() {\n    // write your code here\n    return 0;\n}\n',
  javascript: "// Read input from stdin, print with console.log()\nconst lines = require('fs').readFileSync(0, 'utf8').split('\\n');\n\n"
};

const verdictBadge = (passed, verdict) => (
  <span className={`badge ${passed ? 'bg-success' : 'bg-danger'}`}>{verdict}</span>
);

const OutputBox = ({ label, text }) => (
  <div className="mb-2">
    <div className="small fw-semibold text-muted">{label}</div>
    <pre className="bg-light border rounded p-2 mb-0 small" style={{ whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto' }}>{text === '' ? ' ' : text}</pre>
  </div>
);

/**
 * Coding question UI: problem + samples, editor, Run (sample/custom input) and Submit (all test cases).
 * Props:
 *  token, question, initialDraft {language, sourceCode} | undefined,
 *  initialSubmission {status, passedCount, totalCount, submissionCount} | undefined, maxSubmissions (number),
 *  onSubmitted(questionId, {status, passedCount, totalCount}), onDraftChange(questionId, {language, sourceCode})
 */
const CodingPanel = ({ token, question, initialDraft, initialSubmission, maxSubmissions = 1, initialRunCount = 0, maxRuns = 10, onRunCount, onSubmitted, onDraftChange }) => {
  const languages = (question.codingLanguages && question.codingLanguages.length > 0 ? question.codingLanguages : ALL_LANGUAGES)
    .filter((l) => LANGUAGE_META[l]);
  const firstLang = languages[0] || 'python';

  const [language, setLanguage] = useState(initialDraft?.language && languages.includes(initialDraft.language) ? initialDraft.language : firstLang);
  // one buffer per language so switching language never throws away code
  const [codes, setCodes] = useState(() => ({
    ...(initialDraft?.language ? { [initialDraft.language]: initialDraft.sourceCode } : {})
  }));
  const [useCustom, setUseCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [busy, setBusy] = useState(null); // 'run' | 'submit' | null
  const [error, setError] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [lastSubmission, setLastSubmission] = useState(initialSubmission || null);
  const [submissionCount, setSubmissionCount] = useState(initialSubmission?.submissionCount || 0);
  const limitReached = submissionCount >= maxSubmissions;
  const [runCount, setRunCount] = useState(initialRunCount);
  const runLimitReached = runCount >= maxRuns;
  const [saveState, setSaveState] = useState('');
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const code = codes[language] !== undefined ? codes[language] : STARTERS[language];
  const saveTimer = useRef(null);
  const dirty = useRef(false);
  const latest = useRef({ language, code });
  latest.current = { language, code };

  const samples = question.testCases || [];

  const saveDraftNow = useCallback(async () => {
    const { language: l, code: c } = latest.current;
    if (!c || !c.trim()) return;
    try {
      setSaveState('saving');
      await codeSaveDraft({ token, questionId: question.id, language: l, sourceCode: c });
      setSaveState('saved');
      dirty.current = false;
    } catch (e) {
      setSaveState('error');
    }
  }, [token, question.id]);

  // debounce auto-save
  useEffect(() => {
    if (codes[language] === undefined) return undefined; // nothing typed yet
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveDraftNow, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [codes, language, saveDraftNow]);

  // keep the page-level draft current so coming back to this question restores the code
  const reportDraft = (l, c) => { if (onDraftChange) onDraftChange(question.id, { language: l, sourceCode: c }); };

  const setCode = (value) => {
    dirty.current = true;
    setCodes((prev) => ({ ...prev, [language]: value ?? '' }));
    reportDraft(language, value ?? '');
  };

  const changeLanguage = (l) => {
    setLanguage(l);
    if (codes[l] !== undefined) reportDraft(l, codes[l]);
  };

  // leaving the question (or the page) flushes any unsaved typing
  useEffect(() => () => {
    if (dirty.current) {
      const { language: l, code: c } = latest.current;
      if (c && c.trim()) codeSaveDraft({ token, questionId: question.id, language: l, sourceCode: c }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errMsg = (e) => e.response?.data?.message || e.message || 'Something went wrong';

  const handleRun = async () => {
    setError('');
    setSubmitResult(null);
    setBusy('run');
    try {
      const res = await codeRun({
        token,
        questionId: question.id,
        language,
        sourceCode: code,
        ...(useCustom ? { customInput } : {})
      });
      setRunResult(res.data);
      const n = res.data?.runCount ?? runCount + 1;
      setRunCount(n);
      if (onRunCount) onRunCount(question.id, n);
    } catch (e) {
      setError(errMsg(e));
      if (e.response?.status === 429 && /all \d+ runs/.test(errMsg(e))) setRunCount(maxRuns);
    } finally {
      setBusy(null);
    }
  };

  const handleSubmit = async () => {
    setConfirmSubmit(false);
    setError('');
    setRunResult(null);
    setBusy('submit');
    try {
      clearTimeout(saveTimer.current);
      const res = await codeSubmit({ token, questionId: question.id, language, sourceCode: code });
      const data = res.data;
      setSubmitResult(data);
      const count = data.submissionCount ?? submissionCount + 1;
      const summary = { status: data.status, passedCount: data.passedCount, totalCount: data.totalCount, submissionCount: count };
      setSubmissionCount(count);
      setLastSubmission(summary); // the latest submission is the one that is graded
      if (onSubmitted) onSubmitted(question.id, summary);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="row g-3">
      {/* Problem */}
      <div className="col-12 col-xl-5">
        <div style={{ whiteSpace: 'pre-line' }} className="lh-base mb-3">{question.text}</div>
        {samples.length > 0 && (
          <>
            <h6 className="fw-bold">Sample test cases</h6>
            {samples.map((t, i) => (
              <div key={t.id || i} className="border rounded-3 p-2 mb-2 bg-light">
                <div className="small fw-semibold text-muted">Input</div>
                <pre className="mb-1 small" style={{ whiteSpace: 'pre-wrap' }}>{t.input === '' ? ' ' : t.input}</pre>
                <div className="small fw-semibold text-muted">Expected output</div>
                <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{t.expectedOutput}</pre>
              </div>
            ))}
          </>
        )}
        <div className="small text-muted">
          Limits: {question.timeLimitSeconds ?? 2}s, {question.memoryLimitMb ?? 256} MB. Hidden test cases are used when you Submit.
        </div>
      </div>

      {/* Editor + output */}
      <div className="col-12 col-xl-7">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <select
            className="form-select form-select-sm w-auto"
            value={language}
            onChange={(e) => changeLanguage(e.target.value)}
            disabled={busy !== null}
          >
            {languages.map((l) => <option key={l} value={l}>{LANGUAGE_META[l].label}</option>)}
          </select>
          <div className="small text-muted">
            {saveState === 'saving' && 'Saving…'}
            {saveState === 'saved' && 'Draft saved'}
            {saveState === 'error' && <span className="text-danger">Draft not saved</span>}
          </div>
        </div>

        <div className="border rounded-3 overflow-hidden">
          <Editor
            height="340px"
            language={LANGUAGE_META[language].monaco}
            value={code}
            onChange={setCode}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 4,
              contextmenu: false,
              // Exam mode: no autocomplete or hints of any kind
              quickSuggestions: false,
              suggestOnTriggerCharacters: false,
              wordBasedSuggestions: 'off',
              snippetSuggestions: 'none',
              acceptSuggestionOnEnter: 'off',
              acceptSuggestionOnCommitCharacter: false,
              tabCompletion: 'off',
              parameterHints: { enabled: false },
              hover: { enabled: false },
              inlineSuggest: { enabled: false },
              inlayHints: { enabled: 'off' },
              lightbulb: { enabled: 'off' },
              codeLens: false
            }}
            onMount={(editor, monaco) => {
              // Also block the manual "show suggestions" shortcuts (Ctrl+Space / Cmd+Space / Ctrl+I / Alt+Esc)
              const noop = () => {};
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space, noop);
              editor.addCommand(monaco.KeyMod.WinCtrl | monaco.KeyCode.Space, noop);
              editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyI, noop);
              editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.Escape, noop);
            }}
          />
        </div>

        <div className="form-check mt-2">
          <input className="form-check-input" type="checkbox" id={`custom-${question.id}`} checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} />
          <label className="form-check-label small" htmlFor={`custom-${question.id}`}>Test with custom input</label>
        </div>
        {useCustom && (
          <textarea
            className="form-control form-control-sm mt-1"
            rows={3}
            placeholder="Custom input (stdin)"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            style={{ fontFamily: 'monospace' }}
          />
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
          <button className="btn btn-outline-primary fw-semibold d-flex align-items-center gap-2" onClick={handleRun} disabled={busy !== null || runLimitReached}>
            {busy === 'run' ? <span className="spinner-border spinner-border-sm" /> : <FaPlay />} Run
          </button>
          <button className="btn btn-primary-custom fw-semibold d-flex align-items-center gap-2" onClick={() => setConfirmSubmit(true)} disabled={busy !== null || limitReached}>
            {busy === 'submit' ? <span className="spinner-border spinner-border-sm" /> : <FaPaperPlane />} Submit Code
          </button>
          {lastSubmission && (
            <span className={`small fw-semibold ${lastSubmission.passedCount === lastSubmission.totalCount ? 'text-success' : 'text-muted'}`}>
              Last submission: {lastSubmission.passedCount}/{lastSubmission.totalCount} test cases passed
            </span>
          )}
        </div>

        <div className={`alert ${limitReached || runLimitReached ? 'alert-danger' : 'alert-warning'} border-2 py-2 px-3 mt-2 mb-0`}>
          <div className="fw-bold fs-6">
            You can run your code {maxRuns} times
            <span className={`badge ms-2 ${limitReached || runLimitReached ? 'bg-danger' : 'bg-warning text-dark'}`}>Used {runCount} of {maxRuns}</span>
          </div>
          <div className="fw-semibold small mt-1">
            {limitReached
              ? 'You have submitted this question. Your submission is final.'
              : `You can submit only ONCE, and that submission is final and is the one that is graded.${runLimitReached ? ' You have used all your runs; please submit.' : ''}`}
          </div>
        </div>

        {error && <div className="alert alert-danger py-2 mt-3 mb-0 small">{error}</div>}

        {/* Run output */}
        {runResult && (
          <div className="mt-3">
            <h6 className="fw-bold">{runResult.custom ? 'Output' : 'Sample results'}</h6>
            {runResult.results.map((r) => (
              <div key={r.index} className="border rounded-3 p-2 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small fw-semibold">{runResult.custom ? 'Custom input' : `Sample ${r.index}`}</span>
                  {runResult.custom ? <span className="badge bg-secondary">{r.verdict === 'Accepted' ? 'Ran' : r.verdict}</span> : verdictBadge(r.passed, r.verdict)}
                </div>
                {r.error && <OutputBox label="Error" text={r.error} />}
                <OutputBox label="Your output" text={r.actualOutput} />
                {!runResult.custom && !r.passed && <OutputBox label="Expected output" text={r.expectedOutput} />}
              </div>
            ))}
          </div>
        )}

        {/* Submit output */}
        {submitResult && (
          <div className="mt-3">
            <div className={`alert ${submitResult.status === 'Accepted' ? 'alert-success' : 'alert-warning'} py-2 mb-2`}>
              <strong>{submitResult.status}</strong>: {submitResult.passedCount}/{submitResult.totalCount} test cases passed.
              <span className="ms-2 small text-muted">(Final submission.)</span>
            </div>
            {submitResult.compileError && <OutputBox label="Compilation error" text={submitResult.compileError} />}
            <div className="d-flex flex-wrap gap-2">
              {submitResult.results.map((r) => (
                <span key={r.index} className={`badge ${r.passed ? 'bg-success' : 'bg-danger'} d-flex align-items-center gap-1`} title={r.verdict}>
                  {r.passed ? <FaCheckCircle /> : <FaTimesCircle />} {r.isHidden ? 'Hidden' : 'Sample'} {r.index}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        show={confirmSubmit}
        title="Submit this code?"
        message="You can submit only ONCE for this question, and this submission is final. Submit now?"
        confirmText="Submit"
        onConfirm={handleSubmit}
        onCancel={() => setConfirmSubmit(false)}
      />
    </div>
  );
};

export default CodingPanel;
